import { average, standardDeviation } from "./calculations";

const criteriaUsages = {
  // SAYN 1
  meta_skill: [
    "338a61d0-41f8-40d6-94eb-2fef310d5693", // Both
    "6cbd380d-ca95-413d-9c73-7883051c76cb", // Professional
  ],
  // SAYN 2
  principle: [
    "338a61d0-41f8-40d6-94eb-2fef310d5693", // Both
    "33ae0fb7-364e-494f-8c9f-db291abbd7d1", // Personal
  ],
};

export function groupInfo(data = [], key) {
  const filteredData = data.filter((e) =>
    criteriaUsages[key].includes(e.criteria_usage_id),
  );

  const groupedResults = filteredData.reduce(
    (result, item) => ({
      ...result,
      [item[key]]: [...(result[item[key]] || []), item],
    }),
    {},
  );

  // temporary: sort by number in beggining of name
  const groupsOrder = Object.keys(groupedResults).sort((a, b) => {
    const aMatch = a.match(/^\d+/);
    const bMatch = b.match(/^\d+/);
    const aCompare = aMatch ? aMatch[0] : a;
    const bCompare = bMatch ? bMatch[0] : b;
    return aCompare - bCompare;
  });

  const reactivityAvg = average(groupsOrder.map((e) => groupedResults[e].reactivity));

  const groupedData = groupsOrder.map((entry) => {
    const group = groupedResults[entry];

    const metaAvg = average(group.map((e) => e.reactivity));
    const scoreAvg = average(group.map((e) => e.score));
    const stDeriv = standardDeviation(group.map((e) => e.reactivity));

    return {
      summaryGroup: true,
      name: entry.replace(/^[^A-Za-z]+/, ""),
      stDeriv,
      tolerance: 0.4,
      reactivity: Math.round(metaAvg * 1000) / 1000,
      reactivityList: group.map((e) => e.reactivity * 100),
      reactivityValue: Number(metaAvg * 100).toFixed(1),
      amplitudeValue: Number(stDeriv * 100).toFixed(1),
      criterias: group,
      score: scoreAvg,
      reactivityAbsolute: metaAvg,
      delta: Math.abs(metaAvg * 1 - reactivityAvg),
      average: reactivityAvg,
      avgStDevRatio: stDeriv / reactivityAvg,
    };
  });

  const amplitudeMetaSkillOrdered = [...groupedData];
  amplitudeMetaSkillOrdered.sort((a, b) => b.avgStDevRatio - a.avgStDevRatio);

  const amplitudeMetaSkillOrderedDelta = [...groupedData];
  amplitudeMetaSkillOrderedDelta.sort((a, b) => a.delta - b.delta);

  return {
    groupedData,
    amplitudeMetaSkillOrdered,
    amplitudeMetaSkillOrderedDelta,
  };
}

const INVALID_QUESTIONNAIRE_ANSWER_STATUSES = ["DELETED", "CANCELED"];

/**
 * Reads a questionnaire answer from a V2 GET payload.
 * @param {object} payload API response body
 * @returns {object|undefined}
 */
export function getQuestionnaireAnswerFromPayload(payload) {
  if (!payload) {
    return undefined;
  }

  if (Array.isArray(payload.results)) {
    return payload.results[0];
  }

  if (payload.id) {
    return payload;
  }

  return undefined;
}

/**
 * Whether a public questionnaire-answer link can still be used.
 * Completed answers stay reachable so the completion page can be shown.
 * Disabled invitations and deleted or canceled answers are invalid.
 * @param {object|null|undefined} questionnaireAnswer QA with entityStatus and invites
 * @returns {boolean}
 */
export function isPublicQuestionnaireAnswerLinkValid(questionnaireAnswer) {
  if (!questionnaireAnswer) {
    return false;
  }

  const status =
    questionnaireAnswer.entityStatus && questionnaireAnswer.entityStatus.status;
  if (INVALID_QUESTIONNAIRE_ANSWER_STATUSES.includes(status)) {
    return false;
  }

  if (questionnaireAnswer.completed) {
    return true;
  }

  const invites = questionnaireAnswer.invites || [];
  if (invites.length > 0) {
    return invites.some(
      (invite) => invite.entityStatus && invite.entityStatus.status === "ACTIVE",
    );
  }

  return questionnaireAnswer.from_site === true || questionnaireAnswer.invited === true;
}

export function getUserName(questionnaireAnswer) {
  const { user, pacient_name } = questionnaireAnswer;
  return pacient_name
    ? pacient_name.trim()
    : user.first_name
      ? `${user.first_name} ${user.last_name || ""}`.trim()
      : "";
}

/**
 * Appends the email to a patient name when one is available.
 * @param {string} name - Patient display name.
 * @param {string} [email] - Patient email address.
 * @returns {string} Name with email in parentheses, or the name alone.
 */
export function formatPatientNameWithEmail(name, email) {
  const displayName = (name || "").trim();
  if (!email) {
    return displayName;
  }
  return displayName ? `${displayName} (${email})` : email;
}
